import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const LocationGallerySubmissionContext = createContext();

export const useLocationGallerySubmissions = () => {
  const context = useContext(LocationGallerySubmissionContext);
  if (!context) {
    throw new Error('useLocationGallerySubmissions must be used within a LocationGallerySubmissionProvider');
  }
  return context;
};

export const LocationGallerySubmissionProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for location gallery submission endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getGallerySubmissionsBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/location-gallery-submissions/api/v1/location-gallery-submissions`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/location-gallery-submissions/api/v1/location-gallery-submissions`;
    } else {
      throw new Error('Invalid login type for location gallery submissions access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access location gallery submissions');
    }
  }, [loginType]);

  // Get all images with pagination and filters
  const getImages = useCallback(async (options = {}) => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.status) queryParams.append('status', options.status);
      if (options.locationId) queryParams.append('location_id', options.locationId);
      if (options.uploadSource) queryParams.append('upload_source', options.uploadSource);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.search) queryParams.append('search', options.search);
      if (options.userId) queryParams.append('user_id', options.userId);

      const baseUrl = getGallerySubmissionsBaseUrl();
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGallerySubmissionsBaseUrl]);

  // Get image by ID
  const getImageById = useCallback(async (imageId) => {
    validateAccess();
    
    if (!imageId) {
      throw new Error('Image ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGallerySubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/image/${imageId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get image by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGallerySubmissionsBaseUrl]);

  // Get image statistics
  const getImageStats = useCallback(async () => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGallerySubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get image statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGallerySubmissionsBaseUrl]);

  // Approve image
  const approveImage = useCallback(async (imageId, verdict = null) => {
    validateAccess();
    
    if (!imageId) {
      throw new Error('Image ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGallerySubmissionsBaseUrl();
      const body = verdict ? JSON.stringify({ verdict }) : JSON.stringify({});
      
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/image/${imageId}/approve`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: body,
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to approve image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGallerySubmissionsBaseUrl]);

  // Reject image
  const rejectImage = useCallback(async (imageId, verdict = null) => {
    validateAccess();
    
    if (!imageId) {
      throw new Error('Image ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getGallerySubmissionsBaseUrl();
      const body = verdict ? JSON.stringify({ verdict }) : JSON.stringify({});
      
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/image/${imageId}/reject`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: body,
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to reject image';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getGallerySubmissionsBaseUrl]);

  // Delete image (permanent)
const deleteImage = useCallback(async (imageId) => {
  validateAccess();

  if (!imageId) {
    throw new Error('Image ID is required');
  }

  setLoading(true);
  setError(null);

  try {
    const baseUrl = getGallerySubmissionsBaseUrl();
    const response = await makeAuthenticatedRequest(
      `${baseUrl}/image/${imageId}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    const result = await handleApiResponse(response);
    return result;
  } catch (err) {
    const errorMessage = err.message || 'Failed to delete image';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
}, [makeAuthenticatedRequest, validateAccess, getGallerySubmissionsBaseUrl]);

  // Get pending images
  const getPendingImages = useCallback(async (options = {}) => {
    validateAccess();
    return await getImages({ ...options, status: 'pending' });
  }, [getImages, validateAccess]);

  // Get approved images
  const getApprovedImages = useCallback(async (options = {}) => {
    validateAccess();
    return await getImages({ ...options, status: 'approved' });
  }, [getImages, validateAccess]);

  // Get rejected images
  const getRejectedImages = useCallback(async (options = {}) => {
    validateAccess();
    return await getImages({ ...options, status: 'rejected' });
  }, [getImages, validateAccess]);

  // Get images by status
  const getImagesByStatus = useCallback(async (status, options = {}) => {
    validateAccess();
    return await getImages({ ...options, status });
  }, [getImages, validateAccess]);

  // Get images by location
  const getImagesByLocation = useCallback(async (locationId, options = {}) => {
    validateAccess();
    return await getImages({ ...options, locationId });
  }, [getImages, validateAccess]);

  // Get images by upload source
  const getImagesBySource = useCallback(async (uploadSource, options = {}) => {
    validateAccess();
    return await getImages({ ...options, uploadSource });
  }, [getImages, validateAccess]);

  // Get images by user
  const getImagesByUser = useCallback(async (userId, options = {}) => {
    validateAccess();
    return await getImages({ ...options, userId });
  }, [getImages, validateAccess]);

  // Get images by date range
  const getImagesByDateRange = useCallback(async (dateFrom, dateTo, options = {}) => {
    validateAccess();
    return await getImages({ 
      ...options, 
      dateFrom, 
      dateTo 
    });
  }, [getImages, validateAccess]);

  // Search images
  const searchImages = useCallback(async (searchTerm, options = {}) => {
    validateAccess();
    
    if (!searchTerm || searchTerm.length < 2) {
      throw new Error('Search term must be at least 2 characters');
    }

    return await getImages({ ...options, search: searchTerm });
  }, [getImages, validateAccess]);

  // Export images data
  const exportImagesData = useCallback(async (filters = {}) => {
    validateAccess();

    const exportOptions = {
      ...filters,
      limit: 10000,
      offset: 0,
    };

    const result = await getImages(exportOptions);
    
    const exportData = result.images.map(image => ({
      'Image ID': image.id,
      'Location ID': image.location_id,
      'Location Name': image.location_name || '',
      'Cloudflare Image ID': image.cloudflare_image_id,
      'Image URL': image.cloudflare_image_url,
      'Upload Source': image.upload_source || '',
      'Status': image.image_status || 'pending',
      'Created Date': image.created_at ? new Date(image.created_at).toLocaleDateString() : '',
      'User First Name': image.user_first_name || '',
      'User Last Name': image.user_last_name || '',
    }));

    return {
      data: exportData,
      totalCount: result.images?.length || 0,
      filename: `location_gallery_images_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getImages, validateAccess]);

  // Get image summary
  const getImageSummary = useCallback(async () => {
    validateAccess();

    try {
      const [stats, recentImages] = await Promise.all([
        getImageStats(),
        getImages({ limit: 10 })
      ]);

      return {
        totalImages: stats.total_images,
        imagesByStatus: stats.images_by_status,
        imagesByLocation: stats.images_by_location,
        imagesBySource: stats.images_by_source,
        recentImages: recentImages.images,
        accessLevel: loginType,
        hasImages: stats.total_images > 0,
        pendingCount: stats.images_by_status?.pending || 0,
        approvedCount: stats.images_by_status?.approved || 0,
        rejectedCount: stats.images_by_status?.rejected || 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get image summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getImageStats, getImages, validateAccess, loginType]);

  // Format image data
  const formatImageData = useCallback((image) => {
    const getStatusColor = (status) => {
      const colors = {
        'pending': 'orange',
        'approved': 'green',
        'rejected': 'red',
      };
      return colors[status?.toLowerCase()] || 'gray';
    };

    const getSourceIcon = (source) => {
      const icons = {
        'user_contribution': '👤',
        'business_gallery': '🏢',
        'admin_upload': '⚙️',
      };
      return icons[source] || '📸';
    };

    return {
      ...image,
      statusColor: getStatusColor(image.image_status),
      sourceIcon: getSourceIcon(image.upload_source),
      formattedCreatedDate: image.created_at 
        ? new Date(image.created_at).toLocaleDateString() 
        : 'N/A',
      formattedCreatedTime: image.created_at
        ? new Date(image.created_at).toLocaleTimeString()
        : 'N/A',
      isPending: image.image_status?.toLowerCase() === 'pending',
      isApproved: image.image_status?.toLowerCase() === 'approved',
      isRejected: image.image_status?.toLowerCase() === 'rejected',
      hasVerdict: !!(image.image_verdict && image.image_verdict.trim()),
      fileSize: image.file_size ? `${(image.file_size / 1024).toFixed(2)} KB` : 'N/A',
    };
  }, []);

  // Validation helpers
  const validateImageFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.dateFrom && filters.dateTo && 
        new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      errors.push({ field: 'dateRange', message: 'Date from must be before date to' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Get approval candidates (pending images)
  const getApprovalCandidates = useCallback(async (options = {}) => {
    validateAccess();

    try {
      const pendingImages = await getPendingImages(options);
      
      return {
        ...pendingImages,
        readyForApproval: pendingImages.images?.length || 0,
        totalPending: pendingImages.images?.length || 0,
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get approval candidates';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getPendingImages, validateAccess]);

  // Health check
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/location-gallery-submissions/health`
        : `${API_BASE_URL}/admin/location-gallery-submissions/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Get endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/location-gallery-submissions/api/v1/location-gallery-submissions`
        : `${API_BASE_URL}/admin/location-gallery-submissions/api/v1/location-gallery-submissions`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getImages,
    getImageById,
    getImageStats,
    
    // Approval Actions
    approveImage,
    rejectImage,
    deleteImage,
    
    // Filter Actions
    getPendingImages,
    getApprovedImages,
    getRejectedImages,
    getImagesByStatus,
    getImagesByLocation,
    getImagesBySource,
    getImagesByUser,
    getImagesByDateRange,
    searchImages,
    
    // Utility Actions
    exportImagesData,
    checkHealth,
    getImageSummary,
    getApprovalCandidates,
    
    // Helpers
    clearError,
    validateImageFilters,
    formatImageData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasGalleryAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <LocationGallerySubmissionContext.Provider value={value}>
      {children}
    </LocationGallerySubmissionContext.Provider>
  );
};